import { urlCustomerAddAddressV2, urlCustomerDefaultAddressV2, urlCustomerDeleteAddressV2, urlCustomerUpdateAddressV2 } from "@/api/endpoint";
import { isOtherThemeHappy, isThemeHappy } from "@/event/theme/theme";
import { alertMessageConfirmDeleteAddressHappy, alertMessageConfirmDeleteAddressHappyEmpty } from "@/happyTheme/event/dashboard/address/alertMessageConfirmDeleteAddressHappy";
import { alertMessageEmptyHappy, alertMessageHappy, showAlertMessageHappy } from "@/happyTheme/event/dashboard/alert/AlertMessageHappy";
import { AddressAddSucceededHappy } from "@/happyTheme/event/order/addressAddSucceededHappy";
import { addressSelectedOrderHappy } from "@/happyTheme/event/order/addressSelectedOrderHappy";
import { TimeOutClass } from "@/happyTheme/services/timeout/TimeOutClass";
import { handleShippingCostHappy } from "@/happyTheme/utils/order/handleShippingCostHappy";
import address from "@/storage/address";
import customer from "@/storage/customer";
import { configDeliveryZones } from "@/storage/setting/configDeliveryZones";
import stores from "@/storage/stores";
import { fetchPostAuthorizationCSRFV2 } from "@/utils/fetch/fetchPostAuthorizationCSRFV2";
import { fetchMe } from "@/utils/jwt/fetchMe";
import { getTokenByDevice } from "@/utils/jwt/getTokenByDevice";
import { setTokenDevice } from "@/utils/jwt/setTokenDevice";

export class AddressService {
    timeOut: TimeOutClass
    messageCatchError = {
        title: "Erreur",
        message: "Une erreur est survenue, veuillez réessayer plus tard.",
        class: "error",
        errors: []
    }

    constructor() {
        this.timeOut = TimeOutClass.getInstance();
    }

    public async deleteAddress(addressId: number): Promise<any> {
        try {
            const data = { address_id: addressId };
            const response = await fetchPostAuthorizationCSRFV2(urlCustomerDeleteAddressV2, data);

            if (response.class === "success") {
                response.message = response.message + ", la popup va se fermer dans 3 secondes";
                const addressIndex = address.getAddressData().findIndex(addr => addr.address_id === addressId);
                if (addressIndex !== -1) {
                    address.getAddressData().splice(addressIndex, 1);
                }
            }

            if (isThemeHappy.value || isOtherThemeHappy.value) {
                alertMessageConfirmDeleteAddressHappy.value = response;
                const idTimeOut = setTimeout(() => {
                    alertMessageConfirmDeleteAddressHappy.value = alertMessageConfirmDeleteAddressHappyEmpty;
                }, 2000);
                this.timeOut.setIdTimeout(idTimeOut);
            }
            return response;

        } catch (error) {

            // console.error("Error deleting address:", error);
        }
    }

    public async addAddress(addressData: any): Promise<any> {
        try {
            const user = customer.getCustomerData();
            const store = stores.getStoresData();
            const data = {
                customer_id: user.customer_id,
                company: addressData.company,
                city: addressData.city.city,
                code_postal_id: addressData.city.city_id,
                address: addressData.address,
                address_2: addressData.address_2,
                country_id: addressData.city.country,
                zone_id: addressData.zone.zone_id,
                store_id: store.store_id,
                default: addressData.default
            }

            const response = await fetchPostAuthorizationCSRFV2(urlCustomerAddAddressV2, data);

            if (response.class === "success") {
                const data = getTokenByDevice()

                const result = await fetchMe(data)

                setTokenDevice(result);

                const addressFind = address
                    .getAddressData()
                    .find((item) => item.address_id === result.address_id)
                const deliveryZone = configDeliveryZones.value.find(
                    (zone) => zone.zone_id === addressFind?.zone_id
                )

                if (deliveryZone && addressFind) {
                    addressSelectedOrderHappy.value = {
                        label: `${addressFind.city} - ${addressFind.postcode}`,
                        value: result.address_id,
                        data: addressFind as any,
                        free_price: deliveryZone.free_price,
                        minimum_price: deliveryZone.minimum_price,
                        price: deliveryZone.price
                    }
                    handleShippingCostHappy()
                }

                addressSelectedOrderHappy.value.data = {
                    ...addressSelectedOrderHappy.value.data,
                    address_1: addressFind?.address_1 || '',
                    address_2: addressFind?.address_2 || '',
                    address_id: addressFind?.address_id || 0,
                    company: addressFind?.company || null,
                    customer_id: addressFind?.customer_id || 0,
                    default: addressFind?.default || false,
                    firstname: addressFind?.firstname || '',
                    lastname: addressFind?.lastname || '',
                    postcode: addressFind?.postcode || '',
                }

                AddressAddSucceededHappy.value = true
            }

            if (isThemeHappy.value || isOtherThemeHappy.value) {
                alertMessageHappy.value = response;
                alertMessageHappy.value.title = "Ajouter une adresse";

                const showTimeOut = setTimeout(() => {
                    showAlertMessageHappy.value = true;
                }, 100);
                this.timeOut.setTimeoutWithKey("show", showTimeOut);

                const idTimeOut = setTimeout(() => {
                    showAlertMessageHappy.value = false;
                    setTimeout(() => {
                        alertMessageHappy.value = alertMessageEmptyHappy
                    }, 500)
                }, 2000)
                this.timeOut.setIdTimeout(idTimeOut);
            }
            return response;

        } catch (error) {
            alertMessageHappy.value = this.messageCatchError;

            const showTimeOut = setTimeout(() => {
                showAlertMessageHappy.value = true;
            }, 100);
            this.timeOut.setTimeoutWithKey("show", showTimeOut);

            const idTimeOut = setTimeout(() => {
                showAlertMessageHappy.value = false;
                setTimeout(() => {
                    alertMessageHappy.value = alertMessageEmptyHappy
                }, 500)
            }, 2000)
            this.timeOut.setIdTimeout(idTimeOut);
        }
    }

    public async updateAddress(updateData: any): Promise<any> {
        try {
            const addressFind = configDeliveryZones.value.find((address: any) => address.city_id === updateData.city.city_id || address.city_id === updateData.zone.code_postal_id);
            if (!addressFind) {
                alertMessageHappy.value = {
                    title: "Modifier une adresse",
                    class: "error",
                    message: "Adresse introuvable",
                    errors: []
                };
                return;
            }

            const user = customer.getCustomerData();
            const store = stores.getStoresData();

            const data = {
                address_id: updateData.address_id,
                customer_id: user.customer_id,
                company: updateData.company,
                city: addressFind.city,
                code_postal_id: addressFind.city_id,
                address: updateData.address_1,
                address_2: updateData.address_2,
                country_id: addressFind.country,
                zone_id: addressFind.zone_id,
                store_id: store.store_id,
                default: updateData.default
            }

            const response = await fetchPostAuthorizationCSRFV2(urlCustomerUpdateAddressV2, data);

            if (response.class === "success") {
                const data = getTokenByDevice()

                const result = await fetchMe(data)

                setTokenDevice(result);
            }

            if (isThemeHappy.value || isOtherThemeHappy.value) {
                alertMessageHappy.value = response;
                alertMessageHappy.value.title = "Modifier une adresse";

                const showTimeOut = setTimeout(() => {
                    showAlertMessageHappy.value = true;
                }, 100);
                this.timeOut.setTimeoutWithKey("show", showTimeOut);

                const idTimeOut = setTimeout(() => {
                    showAlertMessageHappy.value = false;
                    setTimeout(() => {
                        alertMessageHappy.value = alertMessageEmptyHappy
                    }, 500)
                }, 2000)
                this.timeOut.setIdTimeout(idTimeOut);
            }
            return response;

        } catch (error) {
            alertMessageHappy.value = this.messageCatchError;

            const showTimeOut = setTimeout(() => {
                showAlertMessageHappy.value = true;
            }, 100);
            this.timeOut.setTimeoutWithKey("show", showTimeOut);

            const idTimeOut = setTimeout(() => {
                showAlertMessageHappy.value = false;
                setTimeout(() => {
                    alertMessageHappy.value = alertMessageEmptyHappy
                }, 500)
            }, 2000)
            this.timeOut.setIdTimeout(idTimeOut);
        }
    }

    public async updateAddressDefault(addressId: number): Promise<any> {
        try {
            const user = customer.getCustomerData();
            const store = stores.getStoresData();
            const data = {
                address_id: addressId,
                customer_id: user.customer_id,
                store_id: store.store_id
            }

            const response = await fetchPostAuthorizationCSRFV2(urlCustomerDefaultAddressV2, data);

            if (response.class === "success") {
                const data = getTokenByDevice()

                const result = await fetchMe(data)

                setTokenDevice(result);
            }

            if (isThemeHappy.value || isOtherThemeHappy.value) {
                alertMessageHappy.value = response;
                alertMessageHappy.value.title = "Adresse par défaut";
                const showTimeOut = setTimeout(() => {
                    showAlertMessageHappy.value = true;
                }, 100);
                this.timeOut.setTimeoutWithKey("show", showTimeOut);

                const idTimeOut = setTimeout(() => {
                    showAlertMessageHappy.value = false;
                    setTimeout(() => {
                        alertMessageHappy.value = alertMessageEmptyHappy
                    }, 500)
                }, 2000)
                this.timeOut.setIdTimeout(idTimeOut);
            }

            return response;

        } catch (error) {
            alertMessageHappy.value = this.messageCatchError;

            const showTimeOut = setTimeout(() => {
                showAlertMessageHappy.value = true;
            }, 100);
            this.timeOut.setTimeoutWithKey("show", showTimeOut);

            const idTimeOut = setTimeout(() => {
                showAlertMessageHappy.value = false;
                setTimeout(() => {
                    alertMessageHappy.value = alertMessageEmptyHappy
                }, 500)
            }, 2000)
            this.timeOut.setIdTimeout(idTimeOut);
        }
    }
}